import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../config';
import { prepareHeaders } from './utils';
import { z } from 'zod';

const serviceCareAgreementResponseSchema = z
    .object({
        account_id: z.string(),
        self_registration: z.boolean(),
    })
    .transform(response => ({
        accountId: response.account_id,
        selfRegistration: response.self_registration,
    }));

const serviceCareAgreementRequestSchema = z.object({
    self_registration: z.boolean(),
});

export type ServiceCareAgreement = z.infer<typeof serviceCareAgreementResponseSchema>;

export const serviceCareApi = createApi({
    reducerPath: 'rtkq/serviceCareApi',
    baseQuery: fetchBaseQuery({
        baseUrl: config.backend.serviceCareActivation,
        prepareHeaders,
    }),
    tagTypes: ['ServiceCareAgreement'],
    endpoints: builder => ({
        getServiceCareAgreement: builder.query<ServiceCareAgreement, { accountId: string }>({
            query: ({ accountId }) => ({
                url: `/account/${accountId}/agreement`,
            }),
            transformResponse: (response: unknown) => serviceCareAgreementResponseSchema.parse(response),
            providesTags: result =>
                result ? [{ type: 'ServiceCareAgreement', id: result.accountId }] : ['ServiceCareAgreement'],
        }),
        putServiceCareAgreement: builder.mutation<
            ServiceCareAgreement,
            { accountId: string; selfRegistration: boolean }
        >({
            query: ({ accountId, selfRegistration }) => ({
                url: `/account/${accountId}/agreement`,
                method: 'PUT',
                body: serviceCareAgreementRequestSchema.parse({ self_registration: selfRegistration }),
            }),
            transformResponse: (response: unknown) => serviceCareAgreementResponseSchema.parse(response),
            invalidatesTags: result =>
                result ? [{ type: 'ServiceCareAgreement', id: result.accountId }] : ['ServiceCareAgreement'],
        }),
    }),
});

export const { useGetServiceCareAgreementQuery, usePutServiceCareAgreementMutation } = serviceCareApi;
